import { AxiosError } from 'axios';

import { RocketError, getAxiosErrorResponseMessage } from '@shared/api/errors';

import type { FileRouteTypes } from '../routeTree.gen';

export {
  RocketError,
  buildRocketErrorMessage,
  getAxiosErrorResponseMessage,
  shouldRetryQueryOnError,
} from '@shared/api/errors';
export type { RocketErrorDetails, MapAxiosErrorToRocketError } from '@shared/api/errors';

// The `/v1` prefix is optional to support both hand-written hooks and generated hooks.
const ignoreAuthErrorEndpoints = [
  /^(\/v1)?\/sites\/users\/login/,
  /^(\/v1)?\/login/,
  /^(\/v1)?\/sites\/users\/.+\/password/,
  /^(\/v1)?\/account\/password/,
];

const ignoreAllErrorsEndpoints = [/rocket\.net\/blog/];

const accountMeEndpoint = /^(\/v1)?\/?account\/me$/;

export const SITE_NOT_FOUND_ERROR_UUID = '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d';

const isSiteNotFoundError = (error: AxiosError): boolean => {
  const status = error.response?.status;
  return (
    status === 404 &&
    !!(error.response?.data as { errors?: unknown[] })?.errors?.find(
      (e: unknown) => e === SITE_NOT_FOUND_ERROR_UUID
    )
  );
};

export enum RocketErrorKey {
  DataFetchingError = 'data_fetching_error',
  AuthenticationError = 'authentication_error',
  DashboardUnavailableError = 'dashboard_unavailable_error',
  AccountError = 'account_error',
  NotFoundError = 'not_found_error',
}

export const mapAxiosErrorToRocketError = (
  error: AxiosError,
  type: 'mutation' | 'query',
  meta?: Record<string, unknown>
): RocketError | null => {
  const url = error.config?.url || '';
  const status = error.response?.status;

  const ignoreErrors = ignoreAllErrorsEndpoints.some(regExp => regExp.test(url));
  if (ignoreErrors) {
    return null;
  }

  const matchesWhitelistedEndpoints = ignoreAuthErrorEndpoints.some(regExp => regExp.test(url));
  const isAuthError =
    (url && status === 401 && !matchesWhitelistedEndpoints) ||
    (accountMeEndpoint.test(url) && status === 404 && type === 'query');

  if (isAuthError) {
    return new RocketError(RocketErrorKey.AuthenticationError);
  } else if (status === 502 || !status) {
    // Dashboard is unavailable if we get a 502 error or do not receive any response
    return new RocketError(RocketErrorKey.DashboardUnavailableError);
  } else if (accountMeEndpoint.test(url) && status && status >= 500) {
    return new RocketError(RocketErrorKey.AccountError);
  } else if (isSiteNotFoundError(error)) {
    return new RocketError(RocketErrorKey.NotFoundError);
  } else if (type === 'query' && status !== 404) {
    const queryErrorPrefix = typeof meta?.errorMessage === 'string' ? meta.errorMessage : undefined;
    const responseErrorMessage = getAxiosErrorResponseMessage(error);

    return new RocketError(RocketErrorKey.DataFetchingError, {
      queryErrorPrefix,
      responseErrorMessage,
    });
  }

  return null;
};

export const getRocketErrorRedirectPath = (errorKey: string): FileRouteTypes['to'] | null => {
  switch (errorKey as RocketErrorKey) {
    case RocketErrorKey.AuthenticationError:
      return '/n/authentication-error-message';
    case RocketErrorKey.DashboardUnavailableError:
      return '/n/dashboard-unavailable-message';
    case RocketErrorKey.AccountError:
      return '/n/account-error-message';
    case RocketErrorKey.NotFoundError:
      return '/n/not-found';
    case RocketErrorKey.DataFetchingError:
      return null;
    default:
      return null;
  }
};
